import { useEffect, useRef, useState } from "react";
import type { PointerEvent as RPE } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Crosshair, RotateCcw } from "lucide-react";
import { useDesign } from "../state/designStore";

/* تحكم احترافي بالدوران (قرص بتدريج + مغناطيس كل 15° + إدخال رقمي + أزرار سريعة)
   وتحكم بالموضع (D-pad بخطوة قابلة للتغيير مع تكرار عند الضغط المستمر). */

const norm = (d: number) => {
  const x = ((((d + 180) % 360) + 360) % 360) - 180;
  return x === -180 ? 180 : x;
};

function usePos() {
  const { activeText, activeEffect, updateActivePosition } = useDesign();
  const position = activeText?.position ?? activeEffect?.position ?? null;
  return { position, updateActivePosition };
}

const SIZE = 176;
const C = SIZE / 2;
const R = 58;

function pt(deg: number, r: number): [number, number] {
  const a = (deg * Math.PI) / 180; // 0° = أعلى، موجب = باتجاه عقارب الساعة
  return [C + r * Math.sin(a), C - r * Math.cos(a)];
}

export function RotationControl() {
  const { position, updateActivePosition } = usePos();
  const dialRef = useRef<SVGSVGElement>(null);
  const [dragging, setDragging] = useState(false);
  const [txt, setTxt] = useState<string | null>(null); // نص قيد الكتابة (يسمح بـ "-" لحاله)
  if (!position) return null;
  const rot = Math.round(norm(position.rotation));
  const set = (d: number) => updateActivePosition({ rotation: Math.round(norm(d)) });

  const fromPointer = (e: RPE<SVGSVGElement>) => {
    const r = dialRef.current?.getBoundingClientRect();
    if (!r) return;
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    let a = (Math.atan2(dx, -dy) * 180) / Math.PI;
    const snapped = Math.round(a / 15) * 15;
    if (Math.abs(a - snapped) <= 4) a = snapped; // مغناطيس
    set(a);
  };

  const [sx, sy] = pt(0, R);
  const [ex, ey] = pt(rot, R);
  const [kx, ky] = pt(rot, R);
  const [hx, hy] = pt(rot, R - 14);

  const ticks = Array.from({ length: 24 }, (_, i) => i * 15);

  return (
    <div className="flex flex-col gap-4 items-center" dir="ltr">
      <svg
        ref={dialRef}
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="touch-none cursor-grab select-none"
        onPointerDown={(e) => {
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
          setDragging(true);
          fromPointer(e);
        }}
        onPointerMove={(e) => dragging && fromPointer(e)}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        aria-label="قرص الدوران"
      >
        <circle cx={C} cy={C} r={R + 10} fill="rgba(255,255,255,.04)" stroke="rgba(255,255,255,.12)" />
        {ticks.map((d) => {
          const major = d % 45 === 0;
          const [x1, y1] = pt(d, R + 8);
          const [x2, y2] = pt(d, R + (major ? 1 : 4));
          return <line key={d} x1={x1} y1={y1} x2={x2} y2={y2} stroke={major ? "rgba(255,255,255,.6)" : "rgba(255,255,255,.25)"} strokeWidth={major ? 1.6 : 1} />;
        })}
        {rot !== 0 && (
          <path
            d={`M ${sx} ${sy} A ${R} ${R} 0 0 ${rot >= 0 ? 1 : 0} ${ex} ${ey}`}
            fill="none"
            stroke="var(--tg-theme-button-color, #3B82F6)"
            strokeWidth={5}
            strokeLinecap="round"
            opacity={0.85}
          />
        )}
        <line x1={C} y1={C} x2={hx} y2={hy} stroke="rgba(255,255,255,.55)" strokeWidth={2} strokeLinecap="round" />
        <circle cx={kx} cy={ky} r={dragging ? 11 : 9.5} fill="#fff" stroke="var(--tg-theme-button-color, #3B82F6)" strokeWidth={3.5} style={{ transition: "r .12s" }} />
        <text x={C} y={C + 7} textAnchor="middle" fontSize={21} fontWeight={700} fill="#fff" style={{ fontVariantNumeric: "tabular-nums" }}>
          {rot}°
        </text>
        {[
          [0, "0", R - 24],
          [180, "180", R - 24],
          [90, "90", R + 20],
          [-90, "-90", R + 20],
        ].map(([d, l, rr]) => {
          const [x, y] = pt(d as number, rr as number);
          return (
            <text key={String(l)} x={x} y={y + 3} textAnchor="middle" fontSize={8.5} fill="rgba(255,255,255,.45)">
              {l}
            </text>
          );
        })}
      </svg>

      <div className="flex items-center gap-1.5 w-full">
        <Step label="-15°" onClick={() => set(rot - 15)} />
        <Step label="-1°" onClick={() => set(rot - 1)} />
        <div className="flex-1 min-w-0 flex items-center justify-center rounded-xl glass-pill h-10">
          <input
            type="text"
            inputMode="numeric"
            value={txt ?? String(rot)}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9-]/g, "").slice(0, 4);
              setTxt(raw);
              const v = parseInt(raw, 10);
              if (!Number.isNaN(v)) set(v);
            }}
            onBlur={() => setTxt(null)}
            className="w-full bg-transparent text-center outline-none text-base font-semibold tabular-nums px-1"
            aria-label="زاوية الدوران بالدرجات"
          />
        </div>
        <Step label="+1°" onClick={() => set(rot + 1)} />
        <Step label="+15°" onClick={() => set(rot + 15)} />
      </div>

      <div className="grid grid-cols-5 gap-1.5 w-full">
        {[0, 45, 90, 180, -90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => set(d)}
            className={`h-9 rounded-xl text-xs font-medium tabular-nums flex items-center justify-center gap-1 ${
              rot === d ? "bg-tg-button text-tg-buttonText" : "bg-white/8 border border-white/10"
            }`}
            style={rot === d ? undefined : { background: "rgba(255,255,255,.06)" }}
          >
            {d === 0 && <RotateCcw size={11} />}
            {d}°
          </button>
        ))}
      </div>
    </div>
  );
}

function Step({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-10 min-w-[42px] px-1 rounded-xl glass-pill text-[11px] font-medium tabular-nums active:scale-95 transition-transform"
    >
      {label}
    </button>
  );
}

const STEPS = [
  { v: 2, label: "دقيق" },
  { v: 8, label: "عادي" },
  { v: 24, label: "سريع" },
];

export function MoveControl() {
  const { position, updateActivePosition } = usePos();
  const [step, setStep] = useState(8);
  const posRef = useRef(position);
  posRef.current = position;
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearInterval(timer.current);
  }, []);
  if (!position) return null;

  const nudge = (dx: number, dy: number) => {
    const p = posRef.current;
    if (p) updateActivePosition({ x: p.x + dx * step, y: p.y + dy * step });
  };
  const hold = (dx: number, dy: number) => ({
    onPointerDown: (e: RPE<HTMLButtonElement>) => {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      nudge(dx, dy);
      if (timer.current) clearInterval(timer.current);
      timer.current = setInterval(() => nudge(dx, dy), 90);
    },
    onPointerUp: () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    },
    onPointerCancel: () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    },
  });

  const cell = "h-12 rounded-xl glass-pill flex items-center justify-center active:bg-tg-button active:text-tg-buttonText select-none touch-none";

  return (
    <div className="flex flex-col gap-3" dir="ltr">
      <div className="grid grid-cols-5 gap-2 w-full">
        <button type="button" aria-label="تحريك لليسار" className={cell} {...hold(-1, 0)}>
          <ArrowLeft size={19} strokeWidth={2.25} />
        </button>
        <button type="button" aria-label="تحريك لأعلى" className={cell} {...hold(0, -1)}>
          <ArrowUp size={19} strokeWidth={2.25} />
        </button>
        <button
          type="button"
          aria-label="توسيط"
          onClick={() => updateActivePosition({ x: 0, y: 0 })}
          className="h-12 rounded-xl flex items-center justify-center text-tg-buttonText bg-tg-button active:scale-95 transition-transform"
        >
          <Crosshair size={19} strokeWidth={2.25} />
        </button>
        <button type="button" aria-label="تحريك لأسفل" className={cell} {...hold(0, 1)}>
          <ArrowDown size={19} strokeWidth={2.25} />
        </button>
        <button type="button" aria-label="تحريك لليمين" className={cell} {...hold(1, 0)}>
          <ArrowRight size={19} strokeWidth={2.25} />
        </button>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex rounded-xl p-0.5" style={{ background: "rgba(255,255,255,.06)" }} dir="rtl">
          {STEPS.map((s) => (
            <button
              key={s.v}
              type="button"
              onClick={() => setStep(s.v)}
              className={`px-3 h-8 rounded-[10px] text-xs font-medium ${step === s.v ? "bg-tg-button text-tg-buttonText" : "text-tg-hint"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-tg-hint tabular-nums font-mono">
          X {Math.round(position.x)} · Y {Math.round(position.y)}
        </span>
      </div>
    </div>
  );
}

